import {
  COMPLIANCE_EXCEPTION_LIMITS,
  EXCEPTION_EXCLUDES,
  EXCEPTION_NEEDS_EXPIRY,
  EXCEPTION_STATUS_LABEL,
  complianceFixForTitle,
  complianceGuidance,
  type CheckException,
  type ComplianceExceptionStatus,
  type ComplianceScope,
} from '@bartizo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { recordComplianceException } from '../api/compliance.js';
import { formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Dialog } from '../ui/Dialog.js';
import { SelectField, TextAreaField } from '../ui/Field.js';

/**
 * Deciding about a failing check (P11.20), in the way the tools people already use
 * record it: a status, a reason a colleague can follow, and an end date for any risk
 * carried or fix promised. The raw score always stays beside the adjusted one.
 */

/** Why a check is left to a person, and what to do first; or that Bartizo can fix it. */
export function GuidanceNote({ title }: { title: string }): ReactNode {
  const fix = complianceFixForTitle(title);
  if (fix !== undefined) {
    return (
      <section className="guidance">
        <h4>
          <span className="badge badge-success">Bartizo can fix this</span>
        </h4>
        <p className="text-sm">{fix.does}.</p>
        {fix.restart === 'reboot' && (
          <p className="text-sm">
            <strong>Needs a restart</strong> afterwards; the server’s page shows a notice until it
            has been restarted.
          </p>
        )}
      </section>
    );
  }
  const note = complianceGuidance(title);
  return (
    <section className="guidance">
      <h4>
        <span className="badge badge-muted">{note.label}</span>
      </h4>
      <p className="text-sm">{note.why}</p>
      <p className="text-sm">
        <strong>First step:</strong> {note.firstStep}
      </p>
    </section>
  );
}

const BADGE: Readonly<Record<ComplianceExceptionStatus, string>> = {
  accepted_risk: 'badge-warning',
  not_applicable: 'badge-muted',
  false_positive: 'badge-muted',
  planned: 'badge-accent',
};

/** What was decided, why, by whom and until when; with a way to take it back. */
export function DecisionBadge({
  exception,
  timezone,
  onRemove,
}: {
  exception: CheckException;
  timezone: string;
  onRemove?: (() => void) | undefined;
}): ReactNode {
  const excludes = EXCEPTION_EXCLUDES.includes(exception.status);
  return (
    <section className="decision">
      <h4>
        <span className={`badge ${exception.active ? BADGE[exception.status] : 'badge-danger'}`}>
          {EXCEPTION_STATUS_LABEL[exception.status]}
          {!exception.active && ' · expired'}
        </span>
      </h4>
      <p className="text-sm">{exception.reason}</p>
      <p className="cell-sub">
        {exception.by !== null && `By ${exception.by}`}
        {exception.expiresAt !== null && (
          <span title={formatInstant(exception.expiresAt, { timezone })}>
            {exception.by !== null && ' · '}
            {exception.active ? 'until' : 'ended'} {formatRelative(exception.expiresAt)}
          </span>
        )}
        {exception.active && !excludes && ' · still counts as failing'}
        {!exception.active && ' · counts as failing again; review it'}
      </p>
      {onRemove !== undefined && (
        <Button variant="ghost" onClick={onRemove}>
          Remove this decision
        </Button>
      )}
    </section>
  );
}

const STATUSES: readonly ComplianceExceptionStatus[] = [
  'accepted_risk',
  'not_applicable',
  'false_positive',
  'planned',
];

const STATUS_HINT: Readonly<Record<ComplianceExceptionStatus, string>> = {
  accepted_risk:
    'You understand the risk and carry it until the end date. Leaves the adjusted score; the raw score is unchanged.',
  not_applicable: 'The check does not apply here. Leaves the adjusted score.',
  false_positive: 'The check judged this server wrongly. Leaves the adjusted score.',
  planned: 'A fix is promised by the end date. Still counts as failing.',
};

const DAY_CHOICES = [30, 90, 180, COMPLIANCE_EXCEPTION_LIMITS.maxDays] as const;

export interface DecisionTarget {
  policyId: string;
  checkId: number;
  title: string;
}

/** The form: which status, why, until when, and for which servers. */
export function DecisionDialog({
  target,
  scope,
  scopeName,
  onClose,
}: {
  target: DecisionTarget;
  scope: ComplianceScope;
  scopeName: string;
  onClose: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ComplianceExceptionStatus>('accepted_risk');
  const [reason, setReason] = useState('');
  const [days, setDays] = useState<number>(90);
  const needsEnd = EXCEPTION_NEEDS_EXPIRY.includes(status);
  const long = reason.trim().length >= COMPLIANCE_EXCEPTION_LIMITS.reasonMin;

  const save = useMutation({
    mutationFn: () =>
      recordComplianceException({
        policyId: target.policyId,
        checkId: target.checkId,
        status,
        reason: reason.trim(),
        ...(needsEnd ? { expiresAt: new Date(Date.now() + days * 86_400_000).toISOString() } : {}),
        scope,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      void queryClient.invalidateQueries({ queryKey: ['compliance-checks'] });
      void queryClient.invalidateQueries({ queryKey: ['compliance-exceptions'] });
      void queryClient.invalidateQueries({ queryKey: ['hardening'] });
      onClose();
    },
  });

  return (
    <Dialog
      title="Record a decision"
      description={`${target.title} On ${scopeName}, for the servers where it fails.`}
      onClose={onClose}
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (long) save.mutate();
        }}
      >
        <SelectField
          label="What this is"
          hint={STATUS_HINT[status]}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as ComplianceExceptionStatus);
          }}
        >
          {STATUSES.map((each) => (
            <option key={each} value={each}>
              {EXCEPTION_STATUS_LABEL[each]}
            </option>
          ))}
        </SelectField>
        <TextAreaField
          label="Why"
          hint="A sentence a colleague could follow in six months. Shown to everyone who can see this server."
          value={reason}
          maxLength={COMPLIANCE_EXCEPTION_LIMITS.reasonMax}
          rows={3}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
        {needsEnd && (
          <SelectField
            label="Review by"
            hint="It counts as failing again from this date, flagged for review."
            value={String(days)}
            onChange={(event) => {
              setDays(Number(event.target.value));
            }}
          >
            {DAY_CHOICES.map((each) => (
              <option key={each} value={each}>
                {each === COMPLIANCE_EXCEPTION_LIMITS.maxDays ? '1 year' : `${String(each)} days`}
              </option>
            ))}
          </SelectField>
        )}
        {save.isError && (
          <Alert tone="danger" title="Could not record it">
            {save.error instanceof Error ? save.error.message : 'Try again in a moment.'}
          </Alert>
        )}
        <div className="row">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            busy={save.isPending}
            busyLabel="Recording"
            disabled={!long}
          >
            Record decision
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** "Record a decision…" for an admin; renders nothing for anyone else. */
export function RecordDecision({
  target,
  scope,
  scopeName,
}: {
  target: DecisionTarget;
  scope: ComplianceScope;
  scopeName: string;
}): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        onClick={() => {
          setOpen(true);
        }}
      >
        Record a decision…
      </Button>
      {open && (
        <DecisionDialog
          target={target}
          scope={scope}
          scopeName={scopeName}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </>
  );
}
